(() => {
  if (customElements.get('preview-states-panel')) return;

  class PreviewStatesPanel extends HTMLElement {
    constructor() {
      super();
      this.attachShadow({ mode: 'open' });
    }

    configure({ storageKey, groups, title = 'Preview states' }) {
      this.settings = { storageKey, groups, title };
      if (this.isConnected) this.render();
    }

    connectedCallback() {
      if (this.settings) this.render();
    }

    disconnectedCallback() {
      this.events?.abort();
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.frame);
    }

    render() {
      this.disconnectedCallback();
      this.events = new AbortController();
      const listen = (target, type, callback) => target.addEventListener(type, callback, { signal: this.events.signal });
      this.shadowRoot.innerHTML = `
        <style>
          :host { position: fixed; display: block; left: 16px; bottom: calc(108px + env(safe-area-inset-bottom, 0px)); z-index: 2147483000; width: min(320px, calc(100vw - 24px)); color: #143250; font: 13px/1.4 system-ui, sans-serif; color-scheme: light; }
          * { box-sizing: border-box; }
          [hidden] { display: none !important; }
          .panel { border: 1px solid #d2dbe5; border-radius: 12px; background: #fff; box-shadow: 0 6px 28px #001f4433; overflow: hidden; }
          header { display: flex; align-items: center; gap: 2px; background: #003d84; color: #fff; padding: 4px; }
          button { font: inherit; cursor: pointer; }
          header button { color: inherit; background: transparent; border: 0; border-radius: 6px; min-height: 40px; }
          header button:hover { background: #ffffff22; }
          .move { width: 36px; flex: none; cursor: grab; touch-action: none; font-size: 23px; user-select: none; }
          :host([dragging]) .move { cursor: grabbing; }
          .toggle { flex: 1; text-align: left; font-weight: 650; display: flex; align-items: center; justify-content: space-between; gap: 8px; padding: 0 10px; }
          .reset { width: 36px; flex: none; font-size: 20px; }
          .body { padding: 14px; max-height: min(65dvh, var(--preview-body-height, 540px)); overflow-y: auto; overscroll-behavior: contain; }
          fieldset { margin: 0 0 14px; padding: 0; border: 0; min-width: 0; }
          fieldset:last-of-type { margin-bottom: 8px; }
          legend { font-weight: 650; padding: 0; margin-bottom: 7px; }
          .choice { display: block; width: 100%; text-align: left; padding: 10px 12px; min-height: 42px; border: 1px solid #b9c8d8; border-radius: 6px; color: #143250; background: #fff; margin-top: 6px; }
          .choice[aria-pressed="true"] { background: #eaf2fc; border-color: #003d84; box-shadow: inset 3px 0 #003d84; font-weight: 600; }
          .choice:disabled { opacity: .5; cursor: default; }
          button:focus-visible { outline: 3px solid #efac42; outline-offset: -3px; }
          .help { margin: 10px 0 0; color: #596b7e; font-size: 11px; }
          .sr { position: absolute; width: 1px; height: 1px; overflow: hidden; clip-path: inset(50%); }
        </style>
        <section class="panel" aria-label="Preview controls">
          <header>
            <button class="move" type="button" aria-label="Move preview panel" aria-describedby="move-help" title="Drag to move; arrow keys also move">⠿</button>
            <button class="toggle" type="button" aria-controls="choices"><span class="title"></span><span class="indicator" aria-hidden="true"></span></button>
            <button class="reset" type="button" aria-label="Reset panel position" title="Reset position">↺</button>
          </header>
          <div class="body" id="choices"><div class="groups"></div><p class="help" id="move-help">Drag the grip to move. Use arrow keys when focused, or Home to reset. Escape collapses.</p></div>
          <span class="sr" role="status" aria-live="polite"></span>
        </section>`;
      this.shadowRoot.querySelector('.title').textContent = this.settings.title;
      const groupContainer = this.shadowRoot.querySelector('.groups');
      this.settings.groups.forEach((group) => {
        const fieldset = document.createElement('fieldset');
        const legend = document.createElement('legend');
        legend.textContent = group.label;
        fieldset.appendChild(legend);
        group.options.forEach((option) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'choice';
          button.textContent = option.label;
          button.dataset.group = group.id;
          button.dataset.value = option.value;
          button.disabled = Boolean(option.disabled);
          button.setAttribute('aria-pressed', String(option.value === group.value));
          fieldset.appendChild(button);
        });
        groupContainer.appendChild(fieldset);
      });
      this.position = null;
      this.open = !matchMedia('(max-width: 749px)').matches;
      try {
        const saved = JSON.parse(localStorage.getItem(this.settings.storageKey));
        if (saved && Date.now() - saved.timestamp < 30 * 86400000) {
          if (Number.isFinite(saved.x) && Number.isFinite(saved.y)) this.position = { x: saved.x, y: saved.y };
          if (typeof saved.open === 'boolean') this.open = saved.open;
        }
      } catch (_) {}
      this.updateOpen();
      const handle = this.shadowRoot.querySelector('.move');
      listen(this.shadowRoot.querySelector('.toggle'), 'click', () => this.setOpen(!this.open));
      listen(this.shadowRoot.querySelector('.reset'), 'click', () => this.reset());
      listen(groupContainer, 'click', (event) => {
        const button = event.target.closest('.choice');
        if (!button || button.disabled) return;
        this.setValue(button.dataset.group, button.dataset.value);
        this.dispatchEvent(new CustomEvent('preview-state-change', {
          detail: { group: button.dataset.group, value: button.dataset.value }, bubbles: true, composed: true,
        }));
      });
      listen(handle, 'pointerdown', (event) => {
        if (event.button !== 0 || !event.isPrimary) return;
        const rect = this.getBoundingClientRect();
        this.drag = { id: event.pointerId, x: event.clientX, y: event.clientY, left: rect.left, top: rect.top };
        handle.setPointerCapture(event.pointerId);
        this.setAttribute('dragging', '');
      });
      listen(handle, 'pointermove', (event) => {
        if (!this.drag || event.pointerId !== this.drag.id) return;
        this.position = { x: this.drag.left + event.clientX - this.drag.x, y: this.drag.top + event.clientY - this.drag.y };
        this.place();
      });
      const endDrag = () => {
        this.drag = null;
        this.removeAttribute('dragging');
        this.save();
      };
      listen(handle, 'pointerup', endDrag);
      listen(handle, 'pointercancel', endDrag);
      listen(handle, 'lostpointercapture', endDrag);
      listen(handle, 'keydown', (event) => {
        if (event.key === 'Home') { event.preventDefault(); this.reset(); return; }
        const directions = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
        const direction = directions[event.key];
        if (!direction) return;
        event.preventDefault();
        const rect = this.getBoundingClientRect();
        const step = event.shiftKey ? 40 : 10;
        this.position = { x: rect.left + direction[0] * step, y: rect.top + direction[1] * step };
        this.place();
        this.save();
      });
      listen(this.shadowRoot, 'keydown', (event) => {
        if (event.key !== 'Escape' || !this.open) return;
        event.preventDefault();
        event.stopPropagation();
        this.setOpen(false);
        this.shadowRoot.querySelector('.toggle').focus();
      });
      const resize = () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.place());
      };
      listen(window, 'resize', resize);
      if (window.visualViewport) {
        listen(window.visualViewport, 'resize', resize);
        listen(window.visualViewport, 'scroll', resize);
      }
      this.resizeObserver = new ResizeObserver(resize);
      this.resizeObserver.observe(this.shadowRoot.querySelector('.panel'));
      this.place();
    }

    setValue(group, value) {
      const setting = this.settings.groups.find((item) => item.id === group);
      if (setting) setting.value = value;
      this.shadowRoot.querySelectorAll('.choice').forEach((button) => {
        if (button.dataset.group === group) button.setAttribute('aria-pressed', String(button.dataset.value === value));
      });
    }

    updateOpen() {
      this.shadowRoot.querySelector('.toggle').setAttribute('aria-expanded', String(this.open));
      this.shadowRoot.querySelector('.indicator').textContent = this.open ? '−' : '+';
      this.shadowRoot.querySelector('.body').hidden = !this.open;
    }

    setOpen(open) {
      this.open = open;
      this.updateOpen();
      this.place();
      this.save();
    }

    place() {
      const viewport = window.visualViewport;
      const left = (viewport?.offsetLeft || 0) + 12;
      const top = (viewport?.offsetTop || 0) + 12;
      const width = viewport?.width || innerWidth;
      const height = viewport?.height || innerHeight;
      this.style.maxWidth = `${Math.max(0, width - 24)}px`;
      this.style.setProperty('--preview-body-height', `${Math.max(60, height - 80)}px`);
      const rect = this.getBoundingClientRect();
      const target = this.position || { x: left + 4, y: top + height - 120 - rect.height };
      const x = Math.max(left, Math.min(target.x, left + width - 24 - rect.width));
      const y = Math.max(top, Math.min(target.y, top + height - 24 - rect.height));
      this.style.left = `${x}px`;
      this.style.top = `${y}px`;
      this.style.bottom = 'auto';
      if (this.position) this.position = { x, y };
    }

    reset() {
      this.position = null;
      this.place();
      this.save();
      this.shadowRoot.querySelector('[role="status"]').textContent = 'Panel position reset.';
    }

    save() {
      try {
        localStorage.setItem(this.settings.storageKey, JSON.stringify({ ...this.position, open: this.open, timestamp: Date.now() }));
      } catch (_) {}
    }
  }

  customElements.define('preview-states-panel', PreviewStatesPanel);
})();
